/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/* eslint-disable no-param-reassign */
import { types } from 'mobx-state-tree';
import Framework from '@greenville/framework';
import * as constants from '../constants/ServiceConstants';
import utils from '../../../common/utils';

const GraphTesterModel = types
  .model('GraphTesterModel', {
    // Execution logs state
    executionLogs: types.maybeNull(types.frozen()),
    simplifiedExecutionLogs: types.maybeNull(types.frozen()),
    executionLogsLoading: types.optional(types.boolean, false),
    executionLogsError: types.maybeNull(types.string),
    executionLogsFromAudit: types.optional(types.boolean, false),

    // LLM logs state
    llmLogs: types.maybeNull(types.frozen()),
    llmLogsLoading: types.optional(types.boolean, false),
    llmLogsError: types.maybeNull(types.string),
    llmStats: types.maybeNull(types.frozen()),

    evaluationCriteria: types.maybeNull(types.frozen()),
    evaluationCriteriaLoading: types.optional(types.boolean, false),
    evaluationCriteriaError: types.maybeNull(types.string),
    evalLensCriteriaCachedRequestId: types.maybeNull(types.string),
    evalLensPendingRequestId: types.maybeNull(types.string)
  })
  .actions((self) => ({
    // Execution logs actions
    getExecutionLogs(payload) {
      self.executionLogs = null;
      self.simplifiedExecutionLogs = null;
      self.executionLogsLoading = true;
      self.executionLogsError = null;
      self.executionLogsFromAudit = false;
      Framework.getEventManager().publish(constants.GET_EXECUTION_LOGS, { payload });
    },

    setExecutionLogs(data, fromAudit = false) {
      self.executionLogs = data?.logs || null;
      self.executionLogsFromAudit = fromAudit;
      try {
        self.simplifiedExecutionLogs = data?.logs && !fromAudit ? utils.generateReadableDataFlow(data.logs) : null;
      } catch (err) {
        console.error('Failed to build simplified execution logs', err);
        self.simplifiedExecutionLogs = null;
      }
      self.executionLogsLoading = false;
      self.executionLogsError = null;
    },

    setExecutionLogsError(error) {
      self.executionLogs = null;
      self.simplifiedExecutionLogs = null;
      self.executionLogsFromAudit = false;
      self.executionLogsError = error;
      self.executionLogsLoading = false;
    },

    clearExecutionLogs() {
      self.executionLogs = null;
      self.simplifiedExecutionLogs = null;
      self.executionLogsLoading = false;
      self.executionLogsError = null;
      self.executionLogsFromAudit = false;
    },

    // LLM logs actions
    getLLMLogs(payload) {
      self.llmLogsLoading = true;
      self.llmLogsError = null;
      Framework.getEventManager().publish(constants.GET_LLM_LOGS, { payload });
    },

    setLLMLogs(data) {
      self.llmLogs = data?.logs || null;
      // Compute LLM stats if llmTransaction exists
      if (data?.logs?.llmTransaction) {
        self.llmStats = utils.computeLLMStats(data.logs.llmTransaction);
      }
      self.llmLogsLoading = false;
      self.llmLogsError = null;
    },

    setLLMLogsError(error) {
      self.llmLogsError = error;
      self.llmLogsLoading = false;
    },

    clearLLMLogs() {
      self.llmLogs = null;
      self.llmStats = null;
      self.llmLogsLoading = false;
      self.llmLogsError = null;
    },

    postRunEvalLensEvaluation(payload) {
      const rid = payload?.requestId != null ? String(payload.requestId) : null;
      if (
        rid != null &&
        rid !== '' &&
        rid === self.evalLensCriteriaCachedRequestId &&
        self.evaluationCriteria != null
      ) {
        return;
      }
      self.evaluationCriteriaLoading = true;
      self.evaluationCriteriaError = null;
      self.evaluationCriteria = null;
      self.evalLensPendingRequestId = rid;
      Framework.getEventManager().publish(constants.POST_RUN_EVAL_LENS_EVALUATION, { payload });
    },

    setEvaluationCriteria(data) {
      self.evaluationCriteria = data;
      self.evaluationCriteriaLoading = false;
      self.evaluationCriteriaError = null;
      if (self.evalLensPendingRequestId != null) {
        self.evalLensCriteriaCachedRequestId = self.evalLensPendingRequestId;
      }
      self.evalLensPendingRequestId = null;
    },

    setEvaluationCriteriaError(error) {
      self.evaluationCriteriaError = error;
      self.evaluationCriteriaLoading = false;
      self.evaluationCriteria = null;
      self.evalLensPendingRequestId = null;
    },

    clearEvaluationCriteria() {
      self.evaluationCriteria = null;
      self.evaluationCriteriaLoading = false;
      self.evaluationCriteriaError = null;
      self.evalLensCriteriaCachedRequestId = null;
      self.evalLensPendingRequestId = null;
    },

    // Reset all state
    resetState() {
      self.executionLogs = null;
      self.simplifiedExecutionLogs = null;
      self.executionLogsLoading = false;
      self.executionLogsError = null;
      self.executionLogsFromAudit = false;
      self.llmLogs = null;
      self.llmStats = null;
      self.llmLogsLoading = false;
      self.llmLogsError = null;
      self.evaluationCriteria = null;
      self.evaluationCriteriaLoading = false;
      self.evaluationCriteriaError = null;
      self.evalLensCriteriaCachedRequestId = null;
      self.evalLensPendingRequestId = null;
    }
  }));

export default GraphTesterModel;
